import {
  useScrollPaddingTopForType,
  type ScrollPaddingTopHeaderType
} from '@portal/atoms/scrollPaddingTopAtom';
import type { HeaderResult } from '@portal/types/studio/header';
import type { HeaderFullV2 } from '@portal/types/studio/studio';
import { navigationClickEvent } from 'analytics/events/studio';
import { SessionCustomer, useLogout } from 'appLayer/auth';
import clsx from 'clsx';
import { FC, useEffect, useRef } from 'react';
import { useOnClickOutside } from 'shared/hooks/useOnClickOutside';
import IconX from 'shared/ui/Icon/24/IconX';
import { getSanityLinkUrl } from 'shared/utils/getSanityLinkUrl';
import { getThemeCSSClass } from 'shared/utils/getThemeCSSClass';
import HeaderLogo from '../Logo/Logo';
import HeaderMenuLink from './LinkElement/LinkElement';
import styles from './Navigation.module.scss';
import NavigationItem from './NavigationItem/NavigationItem';

type NavigationProps = {
  header: HeaderResult;
  opened: boolean;
  logoSVG?: string;
  logoLink: string;
  closeMenu: () => void;
  shopLink: HeaderFullV2['shopLink'] | null;
  parentHeaderType?: ScrollPaddingTopHeaderType;
  customer?: SessionCustomer;
} & Pick<HeaderFullV2, 'theme'>;

const Navigation: FC<NavigationProps> = ({
  closeMenu,
  opened,
  header,
  logoSVG,
  logoLink,
  theme,
  shopLink,
  parentHeaderType = 'navigation',
  customer
}) => {
  const navigationRef = useRef<HTMLDivElement>(null);
  const logout = useLogout();
  const contactLink = getSanityLinkUrl(header.contactButtonLink?.link);

  const handleCloseMenu = () => {
    navigationRef.current?.classList.add(styles.closed);
    navigationRef.current?.classList.remove(styles.opened);

    setTimeout(() => {
      closeMenu();
    }, 400);
  };

  const handleExit = () => {
    handleCloseMenu();
    logout();
  };

  const navigationList = shopLink ? [shopLink, ...header.navigation.slice(1)] : header.navigation;

  useEffect(() => {
    navigationRef.current?.classList.add(styles.opened);
  }, [opened]);

  useOnClickOutside(navigationRef, handleCloseMenu);

  const navigationTop = useScrollPaddingTopForType(parentHeaderType, 'css');

  return (
    <div
      ref={navigationRef}
      role="dialog"
      className={clsx(
        styles.navigationWrapper,
        opened ? styles.opened : styles.closed,
        getThemeCSSClass(theme)
      )}
      style={navigationTop ? { top: navigationTop } : undefined}>
      <div className={clsx(styles.navigationHead, styles.padding)}>
        <button type="button" onClick={handleCloseMenu} className={styles.closeButton}>
          <IconX className={styles.closeButton} />
        </button>
        <HeaderMenuLink href={logoLink}>
          <HeaderLogo header={header} logoSVG={logoSVG} isWhite />
        </HeaderMenuLink>
      </div>
      <div className={clsx(styles.navWrapper, styles.padding)}>
        <nav className={styles.nav}>
          {navigationList?.map((item) => (
            <NavigationItem
              key={`${item.navLink}_${item.text}`}
              onClick={handleCloseMenu}
              theme={theme}
              {...item}
            />
          ))}
        </nav>
        <div className={styles.bottomLinks}>
          <HeaderMenuLink
            href="/members"
            className={clsx(styles.accountLink, getThemeCSSClass(theme))}
            onClick={() =>
              navigationClickEvent({
                location: 'header_account',
                navigationType: 'link',
                clickText: customer?.firstName || header.accountButtonText || 'Account'
              })
            }>
            {customer?.firstName || header.accountButtonText || 'Account'}
          </HeaderMenuLink>
          {customer ? (
            <HeaderMenuLink
              href="#"
              className={clsx(styles.contactsLink, getThemeCSSClass(theme))}
              onClick={handleExit}>
              {header.logoutButtonText || 'Logout'}
            </HeaderMenuLink>
          ) : (
            <HeaderMenuLink
              href={contactLink || '/customer-service/'}
              className={clsx(styles.contactsLink, getThemeCSSClass(theme))}>
              {header.contactButtonLink?.label || header.contactButtonText || 'Contact'}
            </HeaderMenuLink>
          )}
        </div>
      </div>
    </div>
  );
};

export default Navigation;
